<script lang="ts">
import {
  AUTH_API_INJECTION_TOKEN,
  ACCOUNTS_API_INJECTION_TOKEN,
  USERS_API_INJECTION_TOKEN,
  APP_CONFIG,
  ExpirationOverlay,
} from '@onereach/auth-ui-module';
import { MIN_ZINDEX, OrToastContainerV3 } from '@onereach/ui-components';
import { storeToRefs } from 'pinia';
import { defineComponent, provide, ref, onBeforeUnmount, onErrorCaptured, watch } from 'vue';
import { RouterView } from 'vue-router';

import AccountsApi from '@/api/AccountsApi';
import AuthApi from '@/api/Auth';
import UsersApi from '@/api/UsersApi';
import { AppPage } from '@/components';
import useI18n from '@/composables/useI18n';
import config from '@/config';
import { TranslatableError } from '@/errors';
import SentryApi from '@/sentry';
import { useAuth, usePermissionsStore, useCategoriesStore } from '@/stores';

import pkg from '../package.json';


export default defineComponent({
  components: {
    ExpirationOverlay,
    AppPage,
    OrToastContainerV3,
    RouterView,
  },
  setup() {
    const authStore = useAuth();
    const permissionsStore = usePermissionsStore();
    const categoriesStore = useCategoriesStore();

    const showError = ref(false);
    const errorText = ref('');
    const errorActionText = ref('');
    const lockScreenUsername = ref('');
    const loading = ref(true);
    let isPreloadErrorNotificationShown = false;

    const { t } = useI18n();
    const {
      authorized: isAuthorized,
      username,
      identityProvider,
      reactiveToken,
      cookiesAnotherUser,
      cookiesNoRights,
      cookiesExpired,
      userId,
      multiUserId,
      accountId,
      role,
    } = storeToRefs(authStore);

    //provide api services to AuthPopover
    provide(AUTH_API_INJECTION_TOKEN, AuthApi);
    provide(ACCOUNTS_API_INJECTION_TOKEN, AccountsApi);
    provide(USERS_API_INJECTION_TOKEN, UsersApi);
    provide(APP_CONFIG, config);

    provide(MIN_ZINDEX, 20);

    const handlePreloadError = () => {
      if (isPreloadErrorNotificationShown) return;

      isPreloadErrorNotificationShown = true;
      errorText.value = t('errors.applicationOutdated');
      errorActionText.value = t('common.reloadPage');
      showError.value = true;
    };

    const handleErrorVisibilityUpdate = (value: boolean) => {
      showError.value = value;
      if (value) return;

      errorActionText.value = '';
      isPreloadErrorNotificationShown = false;
    };

    const reloadPage = () => window.location.reload();

    window.addEventListener('vite:preloadError', handlePreloadError);
    onBeforeUnmount(() => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
    });

    onErrorCaptured((e) => {
      if (cookiesExpired.value) return false;
      if (isPreloadErrorNotificationShown) return false;

      errorActionText.value = '';

      if (e instanceof TranslatableError) {

        errorText.value = t(e.translationKey);
        SentryApi.captureException(e.innerError);
      } else {
        errorText.value = t('errors.commonText');
        SentryApi.captureException(e);
      }
      showError.value = true;
      return false;
    });

    watch(isAuthorized, async (val) => {
      if (!val) return;
      loading.value = true;
      try {
        await permissionsStore.getCurrentPermissions();
        lockScreenUsername.value = await authStore.getUserNameForLockScreen();
        await categoriesStore.getCategories();
      } finally {
        loading.value = false;
      }
    });

    return {
      authStore,
      isAuthorized,
      multiUserId,
      cookiesAnotherUser,
      cookiesNoRights,
      username,
      accountId,
      role,
      authUrl: config.AUTH_UI_URL,
      identityProvider,
      token: reactiveToken,
      userId,
      showError,
      cookiesExpired,
      errorText,
      errorActionText,
      permissionsStore,
      lockScreenUsername,
      loading,
      serviceTranslations: pkg.projectConfig.translations,
      config,
      handleErrorVisibilityUpdate,
      reloadPage,
    };
  },
});
</script>

<template>
  <div class="app h-full">
    <expiration-overlay
      :lock-username="lockScreenUsername"
      :username="username"
      :auth-url="authUrl"
      :identity-provider="identityProvider"
      :token="token"
      :user-id="userId"
      :account-id="accountId"
      :cookies-expired="cookiesExpired"
      :cookies-another-user="cookiesAnotherUser"
      :cookies-no-rights="cookiesNoRights"
      :no-access="permissionsStore.noAccess"
      :login-overlay-disabled="false"
      @on-update-cookie-expiration="authStore.updateCookieExpiration"
    />
    <AppPage
      :account-id="accountId"
      :user-role="role"
      :token="token"
      :config="config"
      :loading="loading"
      :service-translations="serviceTranslations"
      :error-text="errorText"
      :error-action-text="errorActionText"
      :show-error="showError"
      data-test="admin-ui-main-page"
      @error-action="reloadPage"
      @update:show-error="handleErrorVisibilityUpdate"
    >
      <OrToastContainerV3 />
      <router-view />
    </AppPage>
  </div>
</template>

<style lang="scss">
html,
body,
#app,
.app {
  height: 100%;
}
</style>
